import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../api/axios";

const CONTROLLERS_CACHE_KEY = "cachedControllerCodes";
const CONTROLLERS_CACHE_TIMESTAMP_KEY = "controllersCacheTimestamp";
const CACHE_DURATION = 8 * 60 * 60 * 1000; // 8 hours in ms

const initialState = {
  controllers: [],
  controller: "",
  loading: "idle",
  error: null,
};

export const fetchController = createAsyncThunk(
  "controllers/fetchController",
  async (_, { rejectWithValue }) => {
    try {
      const cacheTimestamp = getCacheTime();
      const now = Date.now();
      if (cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
        return rejectWithValue({ reset: false });
      }
      const response = await axiosInstance.get("/v2/controller");
      return response.data;
    } catch (error) {
      return rejectWithValue({ reset: true });
    }
  }
);

const storeTimeStamp = () => {
  const now = Date.now();
  localStorage.setItem(CONTROLLERS_CACHE_TIMESTAMP_KEY, now.toString());
};
const storeControllers = (controllers) => {
  localStorage.setItem(CONTROLLERS_CACHE_KEY, JSON.stringify(controllers));
  storeTimeStamp();
};
const resetCache = () => {
  localStorage.removeItem(CONTROLLERS_CACHE_TIMESTAMP_KEY);
  localStorage.removeItem(CONTROLLERS_CACHE_KEY);
};

const getCacheTime = () =>
  localStorage.getItem(CONTROLLERS_CACHE_TIMESTAMP_KEY);
const getCachedControllers = () => {
  const data = localStorage.getItem(CONTROLLERS_CACHE_KEY);
  if (data) return JSON.parse(data);
  else return [];
};

const loadDataFromCache = (state, resetTime = false) => {
  const cacheData = getCachedControllers();
  if (cacheData) {
    state.controllers = cacheData;
    state.loading = "succeeded";
    if (resetTime) storeTimeStamp();
  } else {
    state.controllers = [];
    state.loading = "failed";
    resetCache();
  }
};

const controllerSlice = createSlice({
  name: "controllers",
  initialState,
  reducers: {
    setController: (state, action) => {
      state.controller = action.payload;
    },
    setControllers: (state, action) => {
      state.controllers = action?.payload || [];
      state.loading = "succeeded";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchController.pending, (state) => {
        state.loading = "loading";
        state.error = null;
      })
      .addCase(fetchController.fulfilled, (state, action) => {
        state.loading = "succeeded";
        const controllers = action.payload?.controller_codes || [];
        if (controllers?.length) {
          const sortedControllers = controllers?.sort((a, b) =>
            a.localeCompare(b)
          );
          state.controllers = sortedControllers;
          storeControllers(sortedControllers);
        } else loadDataFromCache(state, true);
      })
      .addCase(fetchController.rejected, (state, action) => {
        loadDataFromCache(state, action?.payload?.reset);
      });
  },
});
export const { setController, setControllers } = controllerSlice.actions;
export default controllerSlice.reducer;
